import type { EntityId, HexColor, IsoDateTime } from '@miyuki/types';
import { z } from 'zod';

import {
  MAX_COLORS,
  MAX_COLUMNS,
  MAX_NAME_LENGTH,
  MAX_ROWS,
  MIN_COLORS,
  MIN_COLUMNS,
  MIN_ROWS,
} from './pattern.constants';

/** Esquemas de validación del patrón. Única fuente de verdad en runtime. */

export const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'El color debe tener el formato #rrggbb')
  .transform((value) => value.toLowerCase() as HexColor);

export const patternNameSchema = z
  .string()
  .trim()
  .min(1, 'El nombre no puede estar vacío')
  .max(MAX_NAME_LENGTH, `El nombre no puede superar ${MAX_NAME_LENGTH} caracteres`);

const entityIdSchema = z
  .string()
  .min(1)
  .transform((value) => value as EntityId);

const isoDateTimeSchema = z
  .string()
  .refine((value) => !Number.isNaN(Date.parse(value)), 'Fecha ISO-8601 inválida')
  .transform((value) => value as IsoDateTime);

/**
 * El tejido del diseño.
 *
 * Va con `.default('loom')` y no como campo obligatorio, y la diferencia no es
 * cosmética: los diseños guardados antes de esta feature no lo traen, y el
 * adaptador de almacenamiento **omite en silencio** toda entrada que no valide
 * (RF-45 de 001). Un campo obligatorio habría hecho desaparecer la lista entera
 * de diseños de la usuaria sin un solo mensaje de error. Con el defecto, el
 * sobre `version: 1` sigue siendo legible y no hace falta migrador.
 */
export const patternTypeSchema = z.enum(['loom', 'peyote']);

/** Una celda: índice de la paleta, o `null` si está vacía. */
export const cellValueSchema = z.number().int().min(0).nullable();

export const patternRowSchema = z.array(cellValueSchema);

export const paletteSchema = z
  .object({
    colors: z.array(hexColorSchema).min(MIN_COLORS).max(MAX_COLORS),
    declaredCount: z.number().int().min(MIN_COLORS).max(MAX_COLORS),
  })
  .refine((palette) => palette.colors.length <= palette.declaredCount, {
    message: 'La paleta tiene más colores que los declarados',
    path: ['colors'],
  });

/**
 * El agregado completo.
 *
 * El ancho uniforme (I3) y la validez de los índices de color (I4) son
 * relaciones entre campos, así que viven en `superRefine` y no en el tipo de
 * cada campo. Son justamente las dos que atrapan una entrada corrupta cuya
 * forma parece correcta.
 */
export const braceletPatternSchema = z
  .object({
    id: entityIdSchema,
    name: patternNameSchema,
    columns: z.number().int().min(MIN_COLUMNS).max(MAX_COLUMNS),
    rows: z.array(patternRowSchema).min(MIN_ROWS).max(MAX_ROWS),
    palette: paletteSchema,
    patternType: patternTypeSchema.default('loom'),
    // Mismo recurso que `patternType`, y por el mismo motivo: `.default()`
    // atiende al campo ausente —el diseño guardado antes de esta feature abre
    // con avance cero y alternando (RF-56, RF-28c)— y `.catch()` al campo
    // corrupto, que degrada al valor por defecto en vez de invalidar el patrón
    // entero y hacerlo desaparecer de la lista sin un mensaje (RF-58).
    progress: z.number().int().min(0).default(0).catch(0),
    readingAlternates: z.boolean().default(true).catch(true),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .superRefine((pattern, ctx) => {
    const colorCount = pattern.palette.colors.length;

    pattern.rows.forEach((row, rowIndex) => {
      if (row.length !== pattern.columns) {
        ctx.addIssue({
          code: 'custom',
          path: ['rows', rowIndex],
          message: `La fila ${rowIndex + 1} tiene ${row.length} celdas y el diseño declara ${pattern.columns}`,
        });
      }

      row.forEach((cell, columnIndex) => {
        if (cell !== null && cell >= colorCount) {
          ctx.addIssue({
            code: 'custom',
            path: ['rows', rowIndex, columnIndex],
            message: `La celda apunta al color ${cell} y la paleta tiene ${colorCount}`,
          });
        }
      });
    });
  })
  // I8 depende de dos campos a la vez, así que no la puede sostener el tipo de
  // `progress`: un avance de 99 filas es un entero no negativo perfectamente
  // válido, y solo se sabe que sobra al mirar cuántas filas hay. Acotar en vez
  // de rechazar es lo que pide RF-58 —y es lo mismo que hace RF-65 cuando una
  // edición deja el diseño más corto que el avance.
  .transform((pattern) => ({
    ...pattern,
    progress: Math.min(pattern.progress, pattern.rows.length),
  }));

export const createPatternSchema = z.object({
  name: patternNameSchema,
  columns: z.number().int().min(MIN_COLUMNS).max(MAX_COLUMNS),
  colorCount: z.number().int().min(MIN_COLORS).max(MAX_COLORS),
  rowCount: z.number().int().min(MIN_ROWS).max(MAX_ROWS),
  patternType: patternTypeSchema.default('loom'),
});

export const patternThumbnailSchema = z.object({
  columns: z.number().int().min(MIN_COLUMNS).max(MAX_COLUMNS),
  rows: z.array(patternRowSchema),
  colors: z.array(hexColorSchema),
});

export const patternSummarySchema = z.object({
  id: entityIdSchema,
  name: patternNameSchema,
  columns: z.number().int().min(MIN_COLUMNS).max(MAX_COLUMNS),
  rowCount: z.number().int().min(MIN_ROWS).max(MAX_ROWS),
  updatedAt: isoDateTimeSchema,
  thumbnail: patternThumbnailSchema,
  patternType: patternTypeSchema.default('loom'),
});

/*
 * Sobres persistidos. `version` es un literal: una entrada escrita por una
 * versión futura del formato no valida, y el adaptador la trata igual que a
 * una corrupta — la omite, nunca la sobrescribe.
 */

export const storedPatternSchema = z.object({
  version: z.literal(1),
  pattern: braceletPatternSchema,
});

export const storedSummarySchema = z.object({
  version: z.literal(1),
  summary: patternSummarySchema,
});

export const storedDraftSchema = z.object({
  version: z.literal(1),
  pattern: braceletPatternSchema,
  savedFrom: entityIdSchema.nullable(),
  updatedAt: isoDateTimeSchema,
});

export type CreatePatternPayload = z.infer<typeof createPatternSchema>;
